import React from "react";
import useIsMobile from "../../hooks/useIsMobile";
import useIsTab from "../../hooks/useIsTab";
import useWindowSize from "../../hooks/useWindowSize";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useMediaQuery } from "react-responsive";
import { useMeetingAppContext } from "../../MeetingAppContextDef";
import { useSiteContext } from "../../context/SiteContext";
import ElementSection from "../meeting/ElementSection";
import logger from '../../utils/logger';

const LeftSideBarTabView = ({
    height,
    sideBarContainerWidth,
    panelHeight,
    panelHeaderHeight,
    panelHeaderPadding,
    panelPadding,
    handleClose,
    getFolderElementList,
    isRefreshing,
}) => {
    return (
        <div
            className="bg-gray-800"
            style={{
                height,
                width: sideBarContainerWidth,
                paddingTop: panelPadding,
                paddingLeft: panelPadding,
                paddingRight: panelPadding,
                paddingBottom: panelPadding,
            }}
        >
            <div>
                <div
                    className="bg-gray-750"
                    style={{
                        height: height,
                        borderRadius: 10,
                        overflow: "hidden",
                    }}
                >
                    <div
                        className="flex items-center justify-between"
                        style={{
                            padding: panelHeaderPadding,
                            height: panelHeaderHeight - 1,
                            borderBottom: "1px solid #70707033",
                        }}
                    >
                        <p className="text-base text-white font-bold">Elements</p>
                        <button
                            className="text-white"
                            onClick={handleClose}
                            style={{ margin: 0, padding: 0 }}
                        >
                            <XMarkIcon className="h-5 w-5" />
                        </button>
                    </div>
                    <div
                        style={{
                            height: panelHeight,
                            overflowY: "auto",
                        }}
                    >
                        <ElementSection
                            getFolderElementList={getFolderElementList}
                            isRefreshing={isRefreshing}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export function LeftSidebarContainer({
    height,
    sideBarContainerWidth,
    getFolderElementList,
    isRefreshing,
}) {
    const { leftSideBarMode, setLeftSideBarMode } = useMeetingAppContext();
    const { activeElement } = useSiteContext();
    const isMobile = useIsMobile();
    const isTab = useIsTab();
    const windowSize = useWindowSize();
    const isLGDesktop = useMediaQuery({ minWidth: 1024, maxWidth: 1439 });
    const isXLDesktop = useMediaQuery({ minWidth: 1440 });

    const panelPadding = 8;
    const topBarHeight = 64;
    const bottomBarHeight = 60;

    const paddedHeight = height - panelPadding * 3.5;

    // Calculate mobile panel height:
    const mobilePanelHeight = isMobile || isTab ? height - topBarHeight : height;

    const actualMobileHeight =
        isMobile || isTab
            ? (windowSize?.height || window.innerHeight) -
            topBarHeight -
            bottomBarHeight
            : height;

    const panelHeaderHeight = isMobile
        ? 40
        : isTab
            ? 44
            : isLGDesktop
                ? 48
                : isXLDesktop
                    ? 52
                    : 0;

    const panelHeaderPadding = isMobile
        ? 6
        : isTab
            ? 8
            : isLGDesktop
                ? 10
                : isXLDesktop
                    ? 12
                    : 0;

    const handleClose = () => {
        setLeftSideBarMode(null);
    };

    // Check if left CTA is active and should replace sidebar
    let isLeftCtaActive = false;
    try {
        isLeftCtaActive = activeElement?.type === "cta" &&
            activeElement?.settings &&
            JSON.parse(activeElement.settings)?.placement === "left" &&
            !isMobile && !isTab;
    } catch (e) {
        logger("error", "Error parsing CTA settings:", e);
    }

    // Hide left sidebar if left CTA is active and sidebar is closed
    // Show sidebar if sidebar is open (even if CTA is active)
    const shouldHideSidebar = isLeftCtaActive && !leftSideBarMode;

    // Check if website element is active - need higher z-index to show above it
    const isWebsiteActive = activeElement?.type === "website";
    // WebsiteSection uses zIndex: 999999, so sidebar needs to be higher when website is active
    const sidebarZIndex = isWebsiteActive ? 1000000 : 40;
    const mobileSidebarZIndex = isWebsiteActive ? 1000000 : 9999;

    return (
        <>
            {leftSideBarMode && (isTab || isMobile) && (
                <div
                    className="fixed inset-0"
                    style={{
                        opacity: leftSideBarMode ? 1 : 0,
                        pointerEvents: leftSideBarMode ? "auto" : "none",
                        transition: "opacity 0.3s ease-out",
                        zIndex: mobileSidebarZIndex,
                    }}
                >
                    {/* Backdrop */}
                    <div
                        className="fixed inset-0 bg-black bg-opacity-50"
                        onClick={handleClose}
                    />
                    {/* Panel */}
                    <div
                        className="fixed left-0 right-0 bg-gray-800 shadow-xl transform"
                        style={{
                            top: `${topBarHeight}px`,
                            bottom: `${bottomBarHeight}px`,
                            transform: leftSideBarMode ? "translateY(0)" : "translateY(100%)",
                            transition: "transform 0.3s ease-out",
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <LeftSideBarTabView
                            height={"100%"}
                            sideBarContainerWidth={"100%"}
                            panelHeight={Math.max(
                                0,
                                actualMobileHeight -
                                panelHeaderHeight -
                                panelHeaderPadding -
                                panelPadding * 2
                            )}
                            panelHeaderHeight={panelHeaderHeight}
                            panelHeaderPadding={panelHeaderPadding}
                            panelPadding={panelPadding}
                            handleClose={handleClose}
                            getFolderElementList={getFolderElementList}
                            isRefreshing={isRefreshing}
                        />
                    </div>
                </div>
            )}
            {leftSideBarMode && !isMobile && !isTab && !shouldHideSidebar ? (
                <div
                    className="fixed left-0 transition-all duration-300"
                    style={{
                        top: `${topBarHeight}px`,
                        width: `${sideBarContainerWidth}px`,
                        height: `calc(100vh - ${topBarHeight}px)`,
                        zIndex: sidebarZIndex,
                    }}
                >
                    <LeftSideBarTabView
                        height={paddedHeight}
                        sideBarContainerWidth={sideBarContainerWidth}
                        panelHeight={paddedHeight - panelHeaderHeight - panelHeaderPadding}
                        panelHeaderHeight={panelHeaderHeight}
                        panelHeaderPadding={panelHeaderPadding}
                        panelPadding={panelPadding}
                        handleClose={handleClose}
                        getFolderElementList={getFolderElementList}
                        isRefreshing={isRefreshing}
                    />
                </div>
            ) : null}
        </>
    );
}

